// Page feature types — adapted to the real backend (Sprint 7 — Agent P).
//
// Backend `BaseStatus` enum is numeric:
//   Inactive=0, Active=1, Deleted=2, Archived=3, Draft=4, InReview=5,
//   Approved=6, Published=7, Rejected=8
//
// Backend `PageType` enum is numeric:
//   About=1, Help=2, Onboarding=3, Custom=4

import { useTranslation } from 'react-i18next';
import type { ContentStatus } from '@shared/constants/statuses';

// ---- Status -----------------------------------------------------------------

export type PageStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'REJECTED'
  | 'DELETED';

export const PAGE_FILTER_STATUSES: PageStatus[] = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
  'REJECTED',
];

/** Hook resolving page-status labels via the `pages` namespace. */
export function usePageStatusLabel(): (status: PageStatus) => string {
  const { t } = useTranslation('pages');
  const td = t as unknown as (key: string) => string;
  return (status) => td(`status.${status}`);
}

const STATUS_TO_NUM: Record<PageStatus, number> = {
  DELETED: 2,
  ARCHIVED: 3,
  DRAFT: 4,
  IN_REVIEW: 5,
  APPROVED: 6,
  PUBLISHED: 7,
  REJECTED: 8,
};

const NUM_TO_STATUS: Record<number, PageStatus> = {
  2: 'DELETED',
  3: 'ARCHIVED',
  4: 'DRAFT',
  5: 'IN_REVIEW',
  6: 'APPROVED',
  7: 'PUBLISHED',
  8: 'REJECTED',
};

export function pageStatusFromNumber(n: number): PageStatus {
  return NUM_TO_STATUS[n] ?? 'DRAFT';
}

export function pageStatusToNumber(s: PageStatus): number {
  return STATUS_TO_NUM[s];
}

/** Collapse onto the 4-state `ContentStatus` used by the shared StatusBadge. */
export function toContentStatus(s: PageStatus): ContentStatus {
  switch (s) {
    case 'DRAFT':
    case 'REJECTED':
      return 'DRAFT';
    case 'IN_REVIEW':
    case 'APPROVED':
      return 'REVIEW';
    case 'PUBLISHED':
      return 'PUBLISHED';
    case 'ARCHIVED':
    case 'DELETED':
      return 'ARCHIVED';
  }
}

// ---- PageType ---------------------------------------------------------------

export type PageType = 'ABOUT' | 'HELP' | 'ONBOARDING' | 'CUSTOM';

export const PAGE_TYPES: PageType[] = ['ABOUT', 'HELP', 'ONBOARDING', 'CUSTOM'];

/** Hook resolving page-type labels via the `pages` namespace. */
export function usePageTypeLabel(): (type: PageType) => string {
  const { t } = useTranslation('pages');
  const td = t as unknown as (key: string) => string;
  return (type) => td(`type.${type}`);
}

const TYPE_TO_NUM: Record<PageType, number> = {
  ABOUT: 1,
  HELP: 2,
  ONBOARDING: 3,
  CUSTOM: 4,
};

const NUM_TO_TYPE: Record<number, PageType> = {
  1: 'ABOUT',
  2: 'HELP',
  3: 'ONBOARDING',
  4: 'CUSTOM',
};

export function pageTypeFromNumber(n: number): PageType {
  return NUM_TO_TYPE[n] ?? 'CUSTOM';
}

export function pageTypeToNumber(t: PageType): number {
  return TYPE_TO_NUM[t];
}

// ---- Records ----------------------------------------------------------------

/** hreflang alternatifi (BE `HreflangAlternateDto`) — dil kodu → yayınlı slug. */
export interface HreflangAlternate {
  languageCode: string;
  slug: string;
}

/** Full single-page record (matches BE `PageDto`). */
export interface StaticPage {
  id: string;
  title: string;
  slug: string;
  pageType: PageType;
  body: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  /** MOD-08 — comma-separated SEO keywords (additive; null on legacy rows). */
  keywords: string | null;
  allowIndexing: boolean;
  status: PageStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // --- İçerik i18n (MOD-09) ---
  languageId: string;
  /** Çeviri grubu — aynı groupId'yi paylaşan sayfalar birbirinin varyantıdır. */
  groupId: string;
  /** Bu sayfanın grubunda mevcut dil KODLARI (örn. ["tr","en"]). */
  variantLanguageCodes: string[];
  /** Yayınlı varyantların hreflang alternatifleri (SEO). */
  hreflangAlternates: HreflangAlternate[];
}

/** Light row used by the list endpoint (matches BE `PageListItemDto`). */
export interface PageListItem {
  id: string;
  title: string;
  slug: string;
  pageType: PageType;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  status: PageStatus;
  publishedAt: string | null;
  updatedAt: string;
  languageId: string;
  groupId: string;
  variantLanguageCodes: string[];
}

/** Per-page version snapshot (matches BE `PageVersionDto`). */
export interface PageVersion {
  id: string;
  pageId: string;
  versionNumber: number;
  title: string;
  slug: string;
  pageType: PageType;
  body: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

// ---- Query / Write ---------------------------------------------------------

export interface PageListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  pageType?: PageType;
  status?: PageStatus;
  publishedOnly?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  descending?: boolean;
  /** İçerik i18n — belirli bir dile göre süz. */
  languageId?: string;
}

/** Body for POST /pages and PUT /pages/{id}. */
export interface PageWriteInput {
  title: string;
  body: string;
  pageType: PageType;
  slug?: string | null;
  excerpt?: string | null;
  coverImageUrl?: string | null;
  sortOrder?: number;
  metaTitle?: string | null;
  metaDescription?: string | null;
  canonicalTag?: string | null;
  customCode?: string | null;
  /** MOD-08 — comma-separated SEO keywords (max 500 chars). */
  keywords?: string | null;
  allowIndexing?: boolean;
  changeNote?: string | null;
  // --- İçerik i18n (create) — languageId yoksa BE varsayılan dili kullanır;
  // groupId yalnız varyant eklerken gönderilir. Update'te ikisi de yok sayılır. ---
  languageId?: string | null;
  groupId?: string | null;
}
